import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Label,
  Row,
} from "reactstrap";
import Select from "react-select";
import toast from "react-hot-toast";
import {useNavigate, useParams } from "react-router-dom";
import * as Yup from "yup";
import VideoService from "../../services/KangarooServices/VideoService";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import DestinationService from "../../services/KangarooServices/DestinationService";
import UniversityService from "../../services/KangarooServices/UniversityService";
import MediaService from "../../services/KangarooServices/MediaServices";
import MediaModal from "../PagesSection/Component/MediaModal";
import { imageBaseApi } from "../../utilis/imageHelper";

const VideoForm = () => {
  const { id } = useParams();
  
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const [isMediaOpen, setIsMediaOpen] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [initialdata, setInitialData] = useState({});

  const [mediaData, setMediaData] = useState([]);
  const [destinationdata, setDestinationData] = useState([]);
  const [universitydata, setUniversityData] = useState([]);

  const toggleMedia = () => setIsMediaOpen(!isMediaOpen);
  
  const fetchMedia = async (page = 1, size = 1000) => {
    try {
      const response = await MediaService.getList({ page, pageSize: size });
      console.log("Fetched Media:", response);
      setMediaData(response);
    } catch (error) {
      console.log(error);
    }
  };

  // Fetch dropdown data
  useEffect(() => {
    DestinationService.getList().then((res) => setDestinationData(res));
    UniversityService.getList().then((res) => setUniversityData(res));
  }, []);

  useEffect(() => {
    fetchMedia();
  }, []);

  const fetchDetail = async () => {
    setLoading(true);
    try {
      const response = await VideoService.getdetails(id);
      console.log("API Response from getdetails:", response);
      
      // Set initial data
      setInitialData(response);
      
      // Set selected media if thumbnailMedia exists
      if (response?.thumbnailMedia) {
        // Infer mimeType from fileName if not present
        const media = { ...response.thumbnailMedia };
        if (!media.mimeType && media.fileName) {
          const ext = media.fileName.split('.').pop().toLowerCase();
          const mimeTypes = {
            'png': 'image/png',
            'jpg': 'image/jpeg',
            'jpeg': 'image/jpeg',
            'gif': 'image/gif',
            'webp': 'image/webp',
            'svg': 'image/svg+xml',
            'pdf': 'application/pdf'
          };
          media.mimeType = mimeTypes[ext] || 'image/png'; // default to image
        }
        setSelectedMedia(media);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchDetail();
    }
  }, [id]);

  // Set selected media after both mediaData and initialdata are loaded
  useEffect(() => {
    if (initialdata?.thumbnailMedia && !selectedMedia) {
      setSelectedMedia(initialdata.thumbnailMedia);
    }
  }, [initialdata?.thumbnailMedia]);

  // Dropdown mapped data
  const mediaOptions = mediaData?.items?.map((i) => ({
    value: i.id,
    label: i.fileName,
  }));

  const destinationOptions = destinationdata?.map((i) => ({
    value: i.id,
    label: i.name,
  }));

  const universityOptions = universitydata?.map((i) => ({
    value: i.id,
    label: i.name,
  }));

  // Validation Schema
  const validationSchema = Yup.object({
    title: Yup.string().required("Title is required"),
    description: Yup.string().required("Description is required"),
    videoUrl: Yup.string().url("Invalid URL").required("Video URL is required"),
    category: Yup.string().nullable(),
  });

  // Formik
  const formik = useFormik({
    enableReinitialize: true, 
    initialValues: {
      title: initialdata?.title || "",
      description: initialdata?.description || "",
      videoUrl: initialdata?.videoUrl || "",
      thumbnailMediaId: initialdata?.thumbnailMedia?.id || null,
      category: initialdata?.category || "",
      relatedCountryId: initialdata?.relatedCountryId || null,
      relatedUniversityId: initialdata?.relatedUniversityId || null,
      isActive: initialdata?.isActive ?? true,
    },

    validationSchema,

    onSubmit: async (values) => {
      console.log(values);
      try {
        setLoading(true);

        const payload = {
          title: values.title,
          description: values.description,
          videoUrl: values.videoUrl,
          thumbnailMediaId: selectedMedia?.id || null,
          category: values.category || null,
          relatedCountryId: values.relatedCountryId || null,
          relatedUniversityId: values.relatedUniversityId || null,
          isActive: Boolean(values.isActive),
        };

        if (id) {
          await VideoService.update(id, payload);
          toast.success("Video updated successfully");
        } else {
          await VideoService.create(payload);
          toast.success("Video added successfully");
        }
        navigate("/video-list");
      } catch (error) {
        console.log("API ERROR:", error.response?.data || error);
        toast.error(
          error.response?.data?.message || "Failed to save video"
        );
      } finally {
        setLoading(false);
      }
    },
  });

  const breadcrumbItems = [{ title: "Back to List", link: "/videos" }];

  return (
    <Container fluid>
      <div className="page-content">
        <BreadCrumb
          title="Video Form"
          pageTitle="Videos"
          breadcrumbItems={breadcrumbItems}
        />

        <form onSubmit={formik.handleSubmit}>
          <div className="d-flex justify-content-end gap-3 mb-3">
            <button type="submit" className="btn btn-success" disabled={loading}>
              {id ? "Update" : "Create"}
            </button>
            <button
              type="button"
              className="btn btn-danger"
              onClick={() => navigate("/videos")}
              disabled={loading}
            >
              Cancel
            </button>
          </div>

          <Row>
            <Col lg={8}>
              <Card>
                <CardBody>
                  {/* Title */}
                  <div className="mb-3">
                    <Label>
                      Title <span className="text-danger">*</span>
                    </Label>
                    <Input
                      name="title"
                      value={formik.values.title}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                    {formik.touched.title && formik.errors.title && (
                      <div className="text-danger mt-1">
                        {formik.errors.title}
                      </div>
                    )}
                  </div>

                  {/* Description */}
                  <div className="mb-3">
                    <Label>
                      Description <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="textarea"
                      name="description"
                      rows="5"
                      value={formik.values.description}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                    {formik.touched.description && formik.errors.description && (
                      <div className="text-danger mt-1">
                        {formik.errors.description}
                      </div>
                    )}
                  </div>

                  {/* Video URL */}
                  <div className="mb-3">
                    <Label>
                      Video URL <span className="text-danger">*</span>
                    </Label>
                    <Input
                      name="videoUrl"
                      value={formik.values.videoUrl}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      placeholder="https://www.youtube.com/watch?v=..."
                    />
                    {formik.touched.videoUrl && formik.errors.videoUrl && (
                      <div className="text-danger mt-1">
                        {formik.errors.videoUrl}
                      </div>
                    )}
                  </div>

                  {/* Category */}
                  <div className="mb-3">
                    <Label>Category</Label>
                    <Input
                      name="category"
                      value={formik.values.category}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                    {formik.touched.category && formik.errors.category && (
                      <div className="text-danger mt-1">
                        {formik.errors.category}
                      </div>
                    )}
                  </div>

                  {/* Active Status */}
                  <div className="mb-3">
                    <Label check>
                      <Input
                        type="checkbox"
                        name="isActive"
                        checked={formik.values.isActive}
                        onChange={(e) =>
                          formik.setFieldValue("isActive", e.target.checked)
                        }
                      />{" "}
                      <strong>Active</strong>
                    </Label>
                  </div>
                </CardBody>
              </Card>
            </Col>

            <Col>
              <Card>
                <CardBody>
                  {/* Thumbnail Media */}
                  <div className="d-flex justify-content-between align-items-center">
                    <Label style={{ cursor: "pointer" }}>
                      Thumbnail Media 
                    </Label>
                    <button
                      type="button"
                      className="btn btn-success btn-sm"
                      onClick={toggleMedia}
                    >
                      +
                    </button>
                  </div>

                  {selectedMedia ? (
                    <div className="mt-2 text-center">
                      {selectedMedia.mimeType?.startsWith("image") && (
                        <img
                          src={`${imageBaseApi}${selectedMedia?.url}`}
                          alt={selectedMedia.fileName}
                          style={{
                            maxHeight: "200px",
                            maxWidth: "100%",
                            borderRadius: "6px",
                          }}
                        />
                      )}
                      {selectedMedia.mimeType === "application/pdf" && (
                        <a
                          href={selectedMedia.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="d-block mt-2"
                        >
                          📄 {selectedMedia.fileName}
                        </a>
                      )}
                      {!selectedMedia.mimeType?.startsWith("image") &&
                        selectedMedia.mimeType !== "application/pdf" && (
                          <span className="d-block mt-2">
                            ❓ {selectedMedia.fileName}
                          </span>
                        )}
                    </div>
                  ) : (
                    <p className="text-muted mt-2">No media selected</p>
                  )}
                </CardBody>
              </Card>

              <Card>
                <CardBody>
                  {/* Related Country */}
                  <div className="mb-3">
                    <Label>Related Country</Label>
                    <Select
                      name="relatedCountryId"
                      options={destinationOptions}
                      value={destinationOptions?.find(
                        (x) => x.value === formik.values.relatedCountryId
                      )}
                      onChange={(option) =>
                        formik.setFieldValue(
                          "relatedCountryId",
                          option?.value || null
                        )
                      }
                      isClearable
                    />
                  </div>

                  {/* Related University */}
                  <div className="mb-3">
                    <Label>Related University</Label>
                    <Select
                      name="relatedUniversityId"
                      options={universityOptions}
                      value={universityOptions?.find(
                        (x) => x.value === formik.values.relatedUniversityId
                      )}
                      onChange={(option) =>
                        formik.setFieldValue(
                          "relatedUniversityId",
                          option?.value || null
                        )
                      }
                      isClearable
                    />
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </form>

        <MediaModal
          isOpen={isMediaOpen}
          toggle={toggleMedia}
          mediaData={mediaData}
          selectedMedia={selectedMedia}
          setSelectedMedia={setSelectedMedia}
        />
      </div>
    </Container>
  );
};

export default VideoForm;